import { useEffect } from 'react'
import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import {
  AddUserProps,
  EditUserParams,
  User,
  USER_ROLE,
  USER_ROLES
} from '@img-arena/consoles-api-streaming'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { FormikTextField } from '@/components/inputs/FormikTextField'
import { OperatorAutocomplete } from '@/components/inputs/OperatorAutocomplete'
import { SelectBox } from '@/components/inputs/SelectBox'
import { ApiError } from '@/service/ApiError'
import { streamingRestApi } from '@/service/streamingApi'
import { useAuthStore } from '@/stores/useAuthStore'
import type { FormDialogProps } from '@/types/forms'
import { UserRole } from '@/types/streams/role'
import { formikSaveButtonProps, getFormError, onFieldChange } from '@/utils/forms/formik'

import { DGE_ROLES_THAT_NEED_OPERATOR, userFormValidationSchema } from './userForm.validation'

const formatRoles = (option: string) =>
  option === USER_ROLE.IMG
    ? option
    : option
        .replace(/_/g, ' ')
        .toLocaleLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())

export const DGEUserForm = ({
  onClose,
  onSubmit,
  open = false,
  defaultValues
}: FormDialogProps<Omit<User, 'lastLogin'>>) => {
  const { enqueueSnackbar } = useSnackbar()
  const userRole = useAuthStore((state) => state.userRole)
  const isSuperAdmin = userRole === USER_ROLE.SUPER_ADMIN
  const isEditMode = !!defaultValues

  const formik = useFormik({
    initialValues: {
      fullName: defaultValues?.fullName || '',
      email: defaultValues?.email || '',
      mainNumber: defaultValues?.mainNumber || null,
      role: defaultValues?.role || null,
      operator: defaultValues?.operator?.id || null,
      company: defaultValues?.company || null
    },

    validationSchema: userFormValidationSchema.DGE,

    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      if (!values.role) return

      const needOperator = DGE_ROLES_THAT_NEED_OPERATOR.some((role) => role === values.role)

      try {
        setSubmitting(true)

        if (isEditMode) {
          const model = {
            email: values.email,
            fullName: values.fullName,
            mainNumber: values.mainNumber,
            role: values.role,
            id: defaultValues.id
          } satisfies Omit<EditUserParams, 'token'>

          await streamingRestApi.endpoints.users.editUser(model as EditUserParams)
        } else {
          let operator: AddUserProps['data']['operator'] = null

          if (needOperator) {
            if (!values.operator) {
              setFieldError('operator', 'Operator is required')
              throw new Error('Operator is required')
            }

            const operatorId = Number(values.operator)

            if (Number.isNaN(operatorId)) {
              setFieldError('operator', 'Operator is required')
              throw new Error('Operator is required')
            }

            const { data: foundOperator } =
              await streamingRestApi.endpoints.operators.get(operatorId)

            if (!foundOperator) {
              throw new Error('Selected operator was not found')
            }

            // TODO: go back here once operator autocomplete is implemented (and with that probably some clarification will come)
            operator = {
              id: foundOperator.id,
              name: foundOperator.name,
              logoUrl: foundOperator.logoUrl || null,
              mainContactName: foundOperator.mainContactName || null,
              mainContactTel: foundOperator.mainContactTel || null,
              mainContactEmail: foundOperator.mainContactEmail || null,
              maxTraders: foundOperator.maxTraders || 0,
              createdAt: foundOperator.createdAt || '',
              notificationEmails: foundOperator.notificationEmails || null,
              pricePerGB: foundOperator.pricePerGB || 0,
              isPush: foundOperator.isPush || false,
              mailingEmails: foundOperator.mailingEmails || null,
              licenseLimitEmails: foundOperator.licenseLimitEmails || null,
              group: foundOperator.group || ''
            }
          }

          const mainNumberValue =
            typeof values.mainNumber === 'string'
              ? values.mainNumber.trim() === ''
                ? null
                : Number.parseInt(values.mainNumber, 10)
              : (values.mainNumber ?? null)

          const model = {
            company: values.company || null,
            email: values.email,
            fullName: values.fullName,
            mainNumber: mainNumberValue,
            role: values.role as UserRole,
            operator
          } satisfies AddUserProps['data']

          await streamingRestApi.endpoints.users.addUser(model)
        }

        onSubmit?.()
        enqueueSnackbar(`User ${values.fullName} has been ${isEditMode ? 'updated' : 'created'}`, {
          variant: 'success'
        })
        handleClose()
      } catch (e) {
        const baseMessage = `Failed to ${isEditMode ? 'update' : 'create'} user`
        const errorDetails =
          e instanceof Error ? e.message : (e as unknown as ApiError)?.message || ''
        const errorMessage = [baseMessage, errorDetails].filter(Boolean).join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        // Throw the error again to be caught by dialog and doesn't close it
        throw new Error(errorMessage)
      } finally {
        setSubmitting(false)
      }
    }
  })

  useEffect(() => {
    if (formik.touched.role) {
      formik.setFieldValue('operator', null, true)
    }
  }, [formik.values.role])

  const title = isEditMode ? 'Edit User' : 'Add User'

  const handleClose = () => {
    formik.resetForm({
      values: {
        fullName: '',
        email: '',
        role: null,
        operator: null,
        company: '',
        mainNumber: null
      }
    })
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      title={title}
      onClose={handleClose}
      SaveButtonProps={formikSaveButtonProps(formik)}
    >
      <FormikTextField label="Full Name" required name="fullName" formik={formik} />
      <FormikTextField label="Email" required name="email" formik={formik} disabled={isEditMode} />
      <FormikTextField label="Contact Number" name="mainNumber" formik={formik} />

      <SelectBox
        label="Role"
        name="role"
        required
        fullWidth
        value={formik.values.role}
        onChange={(value) => onFieldChange(formik, 'role', value)}
        disabled={isEditMode}
        options={isSuperAdmin ? USER_ROLES : USER_ROLES.filter((role) => role !== USER_ROLE.ADMIN)}
        error={getFormError(formik, 'role')}
        formatInputOption={formatRoles}
      />

      {formik.values.role === USER_ROLE.ENGINEER && (
        <FormikTextField
          label="Company"
          disabled={isEditMode}
          required
          name="company"
          formik={formik}
        />
      )}

      {DGE_ROLES_THAT_NEED_OPERATOR.some((role) => role === formik.values.role) && (
        <OperatorAutocomplete
          defaultValue={formik.values.operator ? String(formik.values.operator) : undefined}
          onChange={(value) => onFieldChange(formik, 'operator', value)}
          required
          disabled={isEditMode}
          error={getFormError(formik, 'operator')}
        />
      )}
    </ConsoleDialog>
  )
}
